import { useState, useEffect, useRef } from "react";
import {
  Search,
  Globe,
  Code,
  Layers,
  UploadCloud,
  ArrowLeft,
  Mail,
  Coffee,
  Shield,
  Cpu,
  Database,
  Activity,
  Users,
  Fingerprint,
  FileText,
  Infinity,
  ShieldCheck,
  X,
  CheckCircle2,
  Lock,
  Download,
  FileUp,
  Share2,
  ChevronDown,
  ChevronUp,
  StopCircle,
} from "lucide-react";
import { TECH_SIGNATURES } from "./techSignatures";

// --- CONFIGURATION ---
const USE_SERVER_API = false;
const CONTACT_EMAIL = "ramishscrapper@gmail.com";

// --- SEO HELPER ---
const useSEO = (title, description) => {
  useEffect(() => {
    document.title = title;
    let metaDesc = document.querySelector("meta[name='description']");
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
  }, [title, description]);
};

// --- CUSTOM STYLES ---
const CustomStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;800&family=JetBrains+Mono:wght@400;700&family=Cookie&display=swap');

    body {
      font-family: 'Inter', sans-serif;
      background-color: #0f172a;
      color: #e2e8f0;
    }

    .font-mono { font-family: 'JetBrains Mono', monospace; }
    .font-cookie { font-family: 'Cookie', cursive; }

    @keyframes scan {
      0% { top: 0%; opacity: 0; }
      10% { opacity: 1; box-shadow: 0 0 15px #06b6d4; }
      90% { opacity: 1; box-shadow: 0 0 15px #8b5cf6; }
      100% { top: 100%; opacity: 0; }
    }
    .scan-line {
      position: absolute;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, transparent, #06b6d4, #8b5cf6, transparent);
      animation: scan 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
      z-index: 10;
    }

    .glass-card {
      background: rgba(30, 41, 59, 0.7);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .glow-text {
      text-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
    }
    
    .animate-fade-in { animation: fadeIn 0.6s ease-out forwards; }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

    /* Custom Scrollbar */
    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #0f172a; }
    ::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: #475569; }
  `}</style>
);

// --- BUY ME A COFFEE WIDGET ---
const BuyMeACoffee = () => (
  <a
    href="https://buymeacoffee.com/stackray"
    target="_blank"
    rel="noopener noreferrer"
    className="ml-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg
               bg-amber-400 text-black font-cookie text-sm
               border border-amber-500 hover:bg-amber-300 transition"
  >
    <Coffee size={16} />
    Buy me a coffee
  </a>
);

// --- LOGIC: TECH DETECTION (Browser Side) ---
// TECH_SIGNATURES imported from ./techSignatures.js

const TLD_MAP = {
  uk: "United Kingdom",
  de: "Germany",
  fr: "France",
  ca: "Canada",
  au: "Australia",
  jp: "Japan",
  br: "Brazil",
  in: "India",
  pk: "Pakistan",
  cn: "China",
  ru: "Russia",
  it: "Italy",
  es: "Spain",
  nl: "Netherlands",
  se: "Sweden",
  no: "Norway",
  fi: "Finland",
  dk: "Denmark",
  pl: "Poland",
  id: "Indonesia",
  tr: "Turkey",
  mx: "Mexico",
  ar: "Argentina",
  za: "South Africa",
  kr: "South Korea",
  vn: "Vietnam",
  th: "Thailand",
  my: "Malaysia",
  sg: "Singapore",
  ph: "Philippines",
  nz: "New Zealand",
  ie: "Ireland",
  ch: "Switzerland",
  at: "Austria",
  be: "Belgium",
  pt: "Portugal",
  gr: "Greece",
  cz: "Czech Republic",
  hu: "Hungary",
  ro: "Romania",
  ua: "Ukraine",
  il: "Israel",
  ae: "United Arab Emirates",
  sa: "Saudi Arabia",
  eg: "Egypt",
  ng: "Nigeria",
  ke: "Kenya",
  io: "Global (Tech)",
  ai: "Global (Tech)",
  co: "Global/Colombia",
  me: "Global/Montenegro",
  tv: "Global/Tuvalu",
  cc: "Global/Cocos",
  com: "Global/USA",
  net: "Global",
  org: "Organization",
  edu: "Education",
  gov: "Government",
  mil: "Military",
};

const fetchWithTimeout = async (resource, options = {}) => {
  const { timeout = 25000 } = options; // Increased timeout for reliability
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(resource, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

const fetchHtmlContent = async (url) => {
  let formattedUrl = url.trim();
  if (!formattedUrl.startsWith("http"))
    formattedUrl = "https://" + formattedUrl;
  const encoded = encodeURIComponent(formattedUrl);

  // Robust Proxy Rotation System
  // If one fails, it automatically tries the next one
  const proxies = [
    // 1. AllOrigins (Reliable JSON wrapper)
    { url: `https://api.allorigins.win/get?url=${encoded}`, type: "json" },
    // 2. CodeTabs (Direct fallback)
    { url: `https://api.codetabs.com/v1/proxy?quest=${encoded}`, type: "text" },
    // 3. CorsProxy.io (Fast but sometimes rate limited)
    { url: `https://corsproxy.io/?${encoded}`, type: "text" },
    // 4. ThingProxy (Backup)
    {
      url: `https://thingproxy.freeboard.io/fetch/${formattedUrl}`,
      type: "text",
    },
  ];

  for (const proxy of proxies) {
    try {
      const response = await fetchWithTimeout(proxy.url);
      if (response.ok) {
        if (proxy.type === "json") {
          const data = await response.json();
          if (data.contents && data.contents.length > 0) return data.contents;
        } else {
          const text = await response.text();
          // Basic validation to ensure we didn't just get an empty error page
          if (text && text.length > 50) return text;
        }
      }
    } catch (e) {
      // Silently fail and try next proxy
      continue;
    }
  }

  // If all proxies fail, throw a friendly error
  throw new Error(
    "Unable to access site. It may be offline or blocking scans."
  );
};

const detectTech = (html, url) => {
  const detected = [];
  let isEcommerce = false;

  TECH_SIGNATURES.forEach((tech) => {
    let confidence = 0;
    tech.patterns.forEach((p) => {
      if (p.regex.test(html)) confidence = Math.max(confidence, p.weight);
    });
    if (confidence > 0) {
      detected.push({ ...tech, confidence });
      if (tech.isEcommerce) isEcommerce = true;
    }
  });

  let country = "Unknown";
  try {
    const hostname = new URL(url.startsWith("http") ? url : `https://${url}`)
      .hostname;
    const tld = hostname.split(".").pop().toLowerCase();
    country = TLD_MAP[tld] || "Global/USA";
  } catch (e) { }

  let uniqueEmails = [];
  try {
    // Improved Email Regex
    // 1. Mailto links (High confidence)
    const mailtoRegex = /href=["']mailto:([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})["']/gi;
    const mailtoMatches = [...html.matchAll(mailtoRegex)].map((m) => m[1]);

    // 2. Text pattern (Medium confidence, filtered heavily)
    // Look for email-like strings surrounded by boundaries or common delimiters
    const rawRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    const rawMatches = html.match(rawRegex) || [];

    const junk = [
      ".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg",
      ".js", ".css", ".woff", ".woff2", ".ttf",
      "sentry.io", "w3.org", "example.com", "google.com",
      "u003e", "2x.png", "1x.png", "placeholder",
      "yourdomain", "email.com", "domain.com",
      "bootstrap", "react", "node_modules",
      "github.com", "facebook.com", "twitter.com", // Social media handles often mistaken
    ];

    uniqueEmails = [
      ...new Set(
        [...mailtoMatches, ...rawMatches].map((e) => e.toLowerCase().trim())
      ),
    ].filter(
      (email) =>
        email.length < 60 &&
        email.length > 5 &&
        email.includes("@") &&
        email.includes(".") &&
        !junk.some((j) => email.includes(j)) &&
        !/^\d+@/.test(email) // Filter out auto-generated numeric emails
    );
  } catch (e) { }

  const metaTitle =
    html.match(/<title>(.*?)<\/title>/i)?.[1] || "No Title Found";

  return {
    technologies: detected.sort((a, b) => b.confidence - a.confidence),
    metadata: {
      country,
      emails: uniqueEmails.slice(0, 8),
      isEcommerce,
      title: metaTitle,
    },
  };
};

// --- COMPONENT: NAVBAR ---
const Navbar = ({ setView }) => (
  <nav className="fixed top-0 w-full z-50 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md transition-all duration-300">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
      <div
        onClick={() => setView("landing")}
        className="flex items-center gap-2 cursor-pointer group"
      >
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-violet-500 rounded-full blur opacity-25 group-hover:opacity-75 transition duration-200"></div>
          <div className="relative bg-slate-900 rounded-full p-1.5 border border-slate-700">
            <Layers size={20} className="text-cyan-400" />
          </div>
        </div>
        <span className="font-bold text-xl tracking-tight text-white">
          Stack
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
            Ray
          </span>
        </span>
        <span className="hidden sm:inline text-[10px] font-mono bg-slate-800 text-cyan-400 px-1.5 py-0.5 rounded ml-2 border border-slate-700">
          FREE TOOL
        </span>
      </div>

      <div className="flex items-center gap-4">
        <BuyMeACoffee />
        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 p-2 rounded-lg hover:bg-slate-800"
        >
          <span className="hidden sm:inline text-sm font-medium">
            Contact Us
          </span>
          <Mail size={20} />
        </a>
      </div>
    </div>
  </nav>
);

// --- COMPONENT: FOOTER ---
const Footer = ({ setView }) => (
  <footer className="border-t border-slate-800 bg-slate-900 py-12 mt-auto">
    <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
      <div className="text-center md:text-left">
        <h3 className="font-bold text-white mb-2">StackRay Free Service</h3>
        <p className="text-slate-500 text-sm">
          Advanced intelligence for developers and marketers.
        </p>
        <p className="text-slate-500 text-sm mt-1">
          Free forever. Unlimited Scans.
        </p>
      </div>
      <div className="flex flex-col items-center md:items-end gap-2">
        <div className="flex gap-4 text-sm font-medium text-slate-400">
          <button
            onClick={() => setView("terms")}
            className="hover:text-cyan-400 transition-colors"
          >
            Terms of Use
          </button>
          <button
            onClick={() => setView("privacy")}
            className="hover:text-cyan-400 transition-colors"
          >
            Privacy Policy
          </button>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="hover:text-cyan-400 transition-colors"
          >
            Contact
          </a>
        </div>
        {/* Email address removed from plain text display as requested */}
      </div>
    </div>
  </footer>
);

// --- COMPONENT: INFO MODALS (Terms/Privacy) ---
const InfoPage = ({ title, content, onBack }) => {
  useSEO(
    `${title} | StackRay`,
    `Read the ${title} for StackRay, the free website technology analyzer and contact finder.`
  );

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 max-w-4xl mx-auto animate-fade-in">
      <button
        onClick={onBack}
        className="group flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
      >
        <ArrowLeft size={16} /> Back
      </button>
      <div className="glass-card p-8 rounded-2xl border-t-4 border-t-cyan-500">
        <h1 className="text-3xl font-bold text-white mb-6">{title}</h1>
        <div className="prose prose-invert prose-slate max-w-none">
          {content.map((p, i) => (
            <p key={i} className="mb-4 text-slate-300 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};

const TERMS_CONTENT = [
  "Welcome to StackRay. By using our service, you agree to these terms. StackRay is a free intelligence tool provided 'as is' without warranties of any kind.",
  "You agree to use this tool responsibly. You may not use StackRay for illegal activities, harassment, or unauthorized data collection. We reserve the right to block IP addresses engaging in abusive behavior.",
  "We utilize public data and client-side analysis to generate reports. While we strive for accuracy, we cannot guarantee 100% precision in technology detection.",
  "Limitation of Liability: In no event shall StackRay be liable for any damages arising out of the use or inability to use this service.",
];

const PRIVACY_CONTENT = [
  "Your privacy is important to us. StackRay does not store the history of URLs you scan. Analysis is performed in real-time.",
  "We do not collect personal information from users of the free tool unless you voluntarily contact us via email.",
  "We use third-party analytics to improve the performance of our website, but this data is anonymized.",
  "If you have any questions about our data practices, please contact us via the contact button.",
];

// --- COMPONENT: FAQ SECTION (New for SEO) ---
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "What is a website tech stack checker?",
      a: "A tech stack checker is a tool that analyzes a website's source code to identify the technologies used to build it, such as the CMS (WordPress, Shopify), programming languages (React, PHP), and analytics tools (Google Analytics).",
    },
    {
      q: "Is StackRay free to use?",
      a: "Yes, StackRay is 100% free to use. You can perform unlimited single URL scans and bulk analysis without creating an account or paying a subscription fee.",
    },
    {
      q: "Can I find the owner's email address of a website?",
      a: "StackRay attempts to find public contact information, including email addresses, hidden within the website's code. However, we only display publicly available data and respect privacy regulations.",
    },
    {
      q: "How accurate is the CMS detection?",
      a: "Our detection engine uses over 2,000 unique signatures to identify Content Management Systems. While highly accurate for popular platforms like WordPress, Wix, and Shopify, custom-built sites may sometimes be harder to classify.",
    },
    {
      q: "Can I check multiple websites at once?",
      a: "Absolutely. Use our Bulk Scan feature to analyze a list of URLs. You can copy-paste a list or upload a .txt/.csv file to process them sequentially and export the results.",
    },
  ];

  return (
    <div className="py-24 bg-slate-950 border-t border-slate-800">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-400">
            Everything you need to know about website analysis and tech stack
            detection.
          </p>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border border-slate-800 rounded-xl bg-slate-900/50 overflow-hidden transition-all"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex justify-between items-center p-6 text-left hover:bg-slate-800/50 transition-colors"
              >
                <span className="font-bold text-white">{faq.q}</span>
                {openIndex === i ? (
                  <ChevronUp className="text-cyan-400" size={20} />
                ) : (
                  <ChevronDown className="text-slate-500" size={20} />
                )}
              </button>
              {openIndex === i && (
                <div className="px-6 pb-6 text-slate-400 text-sm leading-relaxed animate-fade-in">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- COMPONENT: SINGLE ANALYZE VIEW ---
const AnalyzeView = ({ onBack, initialUrl = "" }) => {
  const [url, setUrl] = useState(initialUrl);
  const [status, setStatus] = useState("idle"); // idle, scanning, complete, error
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);

  useSEO(
    status === "complete" && data
      ? `Analysis of ${url} | StackRay`
      : "Analyze Website Tech Stack | StackRay",
    "Deep dive into any website's technology stack. Detect CMS, frameworks, plugins, and find contact emails instantly."
  );

  const addLog = (msg) => setLogs((prev) => [...prev, msg]);

  const handleAnalyze = async (e) => {
    e?.preventDefault();
    if (!url) return;
    setStatus("scanning");
    setLogs([]);
    setData(null);

    let targetUrl = url;
    if (!targetUrl.startsWith("http")) targetUrl = "https://" + targetUrl;

    try {
      addLog(`Initialized socket connection...`);
      await new Promise((r) => setTimeout(r, 300));
      addLog(`Targeting ${targetUrl}...`);

      let result;
      if (USE_SERVER_API) {
        const response = await fetch("/.netlify/functions/analyze", {
          method: "POST",
          body: JSON.stringify({ url: targetUrl }),
        });
        if (!response.ok)
          throw new Error("Analysis server refused connection.");
        result = await response.json();
      } else {
        const html = await fetchHtmlContent(targetUrl);
        addLog(`Handshake successful. Payload received.`);
        addLog(`Parsing DOM structure...`);
        await new Promise((r) => setTimeout(r, 800));
        result = detectTech(html, targetUrl);
      }

      addLog(`Signatures matched: ${result.technologies.length}`);
      addLog(`Extraction complete.`);
      setData(result);
      setStatus("complete");
    } catch (err) {
      setStatus("error");
      addLog(`FATAL: ${err.message}`);
    }
  };

  const shareResult = () => {
    const text = `I just analyzed ${url} with StackRay and found ${data?.technologies.length} technologies! Check it out here: https://stackray.com`;
    if (navigator.share) {
      navigator.share({
        title: `StackRay Analysis: ${url}`,
        text: text,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(text);
      alert("Result link copied to clipboard! Share it with your friends.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto pt-24 pb-20 px-4 sm:px-6 min-h-screen">
      <button
        onClick={onBack}
        className="group flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
      >
        <div className="p-1.5 rounded-lg bg-slate-800 group-hover:bg-cyan-900/50 transition-colors">
          <ArrowLeft size={16} />
        </div>
        <span className="font-mono text-sm">RETURN_TO_BASE</span>
      </button>

      {/* Input Section */}
      <div className="max-w-3xl mx-auto mb-12">
        <div className="glass-card rounded-2xl p-1 pl-6 flex items-center shadow-2xl shadow-cyan-900/10 relative overflow-hidden">
          {status === "scanning" && (
            <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-cyan-500 to-violet-500 animate-[loading_2s_ease-in-out_infinite] w-full"></div>
          )}
          <Globe className="text-slate-500 mr-4 flex-shrink-0" size={24} />
          <input
            type="text"
            placeholder="example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAnalyze()}
            className="flex-1 bg-transparent border-none outline-none text-lg py-4 text-white placeholder-slate-600 font-mono w-full min-w-0"
          />
          <button
            onClick={handleAnalyze}
            disabled={status === "scanning"}
            className="m-1 px-6 sm:px-8 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
          >
            {status === "scanning" ? (
              <Activity className="animate-spin" size={20} />
            ) : (
              <Search size={20} />
            )}
            <span className="hidden sm:inline">SCAN</span>
          </button>
        </div>
      </div>

      {/* Logs / Error */}
      {status === "scanning" && (
        <div className="max-w-2xl mx-auto font-mono text-sm animate-fade-in">
          <div className="bg-slate-950 rounded-t-xl border border-slate-800 p-3 flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
          </div>
          <div className="bg-slate-900/80 border-x border-b border-slate-800 p-6 rounded-b-xl h-64 overflow-hidden relative">
            <div className="scan-line"></div>
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="text-cyan-400/80">
                  <span className="text-slate-600 mr-2">
                    [{new Date().toLocaleTimeString()}]
                  </span>
                  {log}
                </div>
              ))}
              <div className="animate-pulse text-cyan-400">_</div>
            </div>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="max-w-2xl mx-auto bg-red-900/20 border border-red-500/50 p-6 rounded-xl text-center">
          <div className="inline-flex p-3 rounded-full bg-red-900/50 mb-4 text-red-400">
            <X size={24} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Scan Failed</h3>
          <p className="text-slate-400 mb-4">{logs[logs.length - 1]}</p>
          <button
            onClick={handleAnalyze}
            className="text-white hover:text-cyan-400 underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Results Dashboard */}
      {status === "complete" && data && (
        <div className="animate-fade-in space-y-8">
          {/* Header Card */}
          <div className="glass-card rounded-2xl p-6 sm:p-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-cyan-500/10 to-transparent rounded-bl-full"></div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 flex items-center justify-center shadow-inner flex-shrink-0">
                  <span className="text-2xl sm:text-3xl font-bold text-white">
                    {url
                      .replace(/https?:\/\//, "")
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1 truncate">
                    {url.replace(/https?:\/\//, "")}
                  </h2>
                  <p className="text-slate-400 flex items-center gap-2 text-sm sm:text-base truncate">
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${data.metadata.isEcommerce
                        ? "bg-green-500"
                        : "bg-slate-500"
                        }`}
                    ></span>
                    {data.metadata.title.substring(0, 50)}
                    {data.metadata.title.length > 50 ? "..." : ""}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 w-full md:w-auto">
                <div className="bg-slate-900/50 p-3 sm:p-4 rounded-xl border border-slate-800 text-center flex-1 md:min-w-[100px]">
                  <div className="text-[10px] sm:text-xs text-slate-500 uppercase font-bold mb-1">
                    Country
                  </div>
                  <div className="text-white font-medium text-sm sm:text-base">
                    {data.metadata.country}
                  </div>
                </div>
                <button
                  onClick={shareResult}
                  className="bg-gradient-to-br from-cyan-600 to-violet-600 p-3 sm:p-4 rounded-xl border border-cyan-400/30 text-center flex-1 md:min-w-[100px] hover:opacity-90 transition-opacity flex flex-col items-center justify-center group"
                >
                  <Share2 size={18} className="mb-1 text-white" />
                  <div className="text-white text-[10px] sm:text-xs uppercase font-bold">
                    Share Result
                  </div>
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Tech List */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Cpu className="text-violet-500" size={20} /> Detected
                  Technologies
                </h3>
                <span className="text-xs font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded border border-slate-800">
                  CONFIDENCE SCORE
                </span>
              </div>

              {data.technologies.length === 0 ? (
                <div className="p-12 rounded-xl border border-dashed border-slate-700 text-center text-slate-500 bg-slate-900/30">
                  No recognizable technologies found. The site might be static
                  or heavily obfuscated.
                </div>
              ) : (
                <div className="grid gap-4">
                  {data.technologies.map((tech, i) => (
                    <div
                      key={i}
                      className="glass-card p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between group hover:border-cyan-500/30 transition-colors gap-4"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-300 border border-slate-700 flex-shrink-0">
                          {tech.category === "CMS" && <Database size={18} />}
                          {tech.category === "E-commerce" && (
                            <Users size={18} />
                          )}
                          {tech.category === "Framework" && <Code size={18} />}
                          {tech.category === "UI" && <Layers size={18} />}
                          {tech.category === "Analytics" && (
                            <Activity size={18} />
                          )}
                          {tech.category === "Marketing" && <Mail size={18} />}
                          {tech.category === "Hosting" && (
                            <UploadCloud size={18} />
                          )}
                          {tech.category === "CDN" && <Globe size={18} />}
                          {![
                            "CMS",
                            "E-commerce",
                            "Framework",
                            "UI",
                            "Analytics",
                            "Marketing",
                            "Hosting",
                            "CDN",
                          ].includes(tech.category) && (
                              <CheckCircle2 size={18} />
                            )}
                        </div>
                        <div>
                          <h4 className="font-bold text-white">{tech.name}</h4>
                          <span className="text-xs text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded">
                            {tech.category}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="h-1.5 w-full sm:w-24 bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-500 to-violet-500"
                            style={{ width: `${tech.confidence}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-mono text-cyan-400 flex-shrink-0">
                          {tech.confidence}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar: Contacts & Security */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                  <Fingerprint className="text-cyan-500" size={20} /> Contact
                  Intelligence
                </h3>
                <div className="glass-card rounded-xl p-6 space-y-4">
                  {data.metadata.emails.length > 0 ? (
                    <ul className="space-y-3">
                      {data.metadata.emails.map((email, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-3 text-sm text-slate-300 group cursor-pointer hover:text-white"
                        >
                          <Mail
                            size={14}
                            className="text-slate-500 group-hover:text-cyan-400"
                          />
                          <span className="truncate">{email}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-6 text-slate-500 text-sm">
                      <Lock className="mx-auto mb-2 opacity-50" size={24} />
                      No public email addresses found on the main page.
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                  <ShieldCheck className="text-green-500" size={20} /> Security
                  Headers
                </h3>
                <div className="glass-card rounded-xl p-6">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Connection</span>
                      <span className="text-green-400 font-mono">
                        Encrypted (HTTPS)
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">X-Frame-Options</span>
                      <span className="text-slate-500 font-mono">Unknown</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-700/50">
                    <button className="w-full py-2 text-xs font-bold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">
                      VIEW FULL HEADER DUMP
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- COMPONENT: BULK ANALYZE VIEW ---
const BulkAnalyzeView = ({ onBack }) => {
  const [urls, setUrls] = useState("");
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const stopSignal = useRef(false);

  useSEO(
    "Bulk Website Tech Checker | StackRay",
    "Analyze multiple websites at once. Bulk CSV export for detected CMS, technologies, and email addresses."
  );

  const handleBulkScan = async () => {
    if (!urls.trim()) return;

    const urlList = urls
      .split(/[\n,]+/)
      .map((u) => u.trim())
      .filter((u) => u.length > 0);
    if (urlList.length === 0) return;

    setIsProcessing(true);
    setResults([]);
    setProgress(0);
    stopSignal.current = false;

    for (let i = 0; i < urlList.length; i++) {
      if (stopSignal.current) {
        break;
      }

      let targetUrl = urlList[i];
      if (!targetUrl.startsWith("http")) targetUrl = "https://" + targetUrl;

      try {
        let scanResult;
        if (USE_SERVER_API) {
          // Mocking server response for now if config enabled
          scanResult = {
            technologies: [],
            metadata: { country: "Server Mode", emails: [] },
          };
        } else {
          const html = await fetchHtmlContent(targetUrl);
          scanResult = detectTech(html, targetUrl);
        }

        // FIX: Logic to detect CMS OR Ecommerce platforms for the "CMS" column
        const cmsOrEcommerce =
          scanResult.technologies.find(
            (t) => t.category === "CMS" || t.category === "E-commerce"
          )?.name || "-";

        setResults((prev) => [
          ...prev,
          {
            url: urlList[i],
            status: "success",
            tech: scanResult.technologies.length,
            cms: cmsOrEcommerce,
            email: scanResult.metadata.emails[0] || "-",
          },
        ]);
      } catch (e) {
        setResults((prev) => [
          ...prev,
          { url: urlList[i], status: "error", tech: 0, cms: "-", email: "-" },
        ]);
      }

      setProgress(Math.round(((i + 1) / urlList.length) * 100));
      // Artificial delay to prevent rate limiting on the demo proxy
      await new Promise((r) => setTimeout(r, 1000));
    }

    setIsProcessing(false);
  };

  const handleStopScan = () => {
    stopSignal.current = true;
    setIsProcessing(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      // Basic CSV/Text parsing: split by newlines or commas, clean quotes
      const foundUrls = text
        .split(/[\r\n,]+/)
        .map((u) => u.replace(/['"]/g, "").trim())
        .filter((u) => u.length > 0 && u.includes(".")); // Simple validation check

      setUrls((prev) => {
        const current = prev ? prev.trim() + "\n" : "";
        return current + foundUrls.join("\n");
      });
    };
    reader.readAsText(file);
    // Reset input so same file can be selected again if needed
    event.target.value = null;
  };

  const downloadCSV = () => {
    if (results.length === 0) return;

    const headers = "URL,Status,CMS,Tech Count,Email\n";
    const rows = results
      .map(
        (r) =>
          `"${r.url}","${r.status}","${r.cms || "-"}","${r.tech}","${r.email || "-"
          }"`
      )
      .join("\n");

    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stackray_bulk_scan_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto pt-24 pb-20 px-4 sm:px-6 min-h-screen">
      <button
        onClick={onBack}
        className="group flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
      >
        <div className="p-1.5 rounded-lg bg-slate-800 group-hover:bg-cyan-900/50 transition-colors">
          <ArrowLeft size={16} />
        </div>
        <span className="font-mono text-sm">RETURN_TO_BASE</span>
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Input Area */}
        <div className="lg:col-span-1 space-y-6">
          <div className="glass-card p-6 rounded-2xl">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <UploadCloud className="text-cyan-400" size={20} /> Bulk Target
              List
            </h2>
            <p className="text-slate-400 text-sm mb-4">
              Enter URLs (one per line) or upload a file (.txt, .csv).
            </p>

            <div className="flex gap-2 mb-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".txt,.csv"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="flex-1 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg border border-slate-700 transition-colors flex items-center justify-center gap-2"
              >
                <FileUp size={14} /> Import List
              </button>
              <button
                onClick={() => setUrls("")}
                disabled={isProcessing || !urls}
                className="px-3 py-2 bg-slate-800 hover:bg-red-900/30 text-slate-300 hover:text-red-400 text-xs font-bold rounded-lg border border-slate-700 transition-colors"
              >
                Clear
              </button>
            </div>

            <textarea
              className="w-full h-64 bg-slate-900/50 border border-slate-700 rounded-xl p-4 text-sm font-mono text-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none resize-none"
              placeholder="example.com&#10;myshopify.com&#10;wordpress.org"
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              disabled={isProcessing}
            ></textarea>

            <div className="mt-4 space-y-3">
              {isProcessing ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-slate-400">
                    <div className="flex items-center gap-2">
                      <Activity
                        size={14}
                        className="text-cyan-400 animate-spin"
                      />
                      <span>Scanning... {progress}%</span>
                    </div>
                    <button
                      onClick={handleStopScan}
                      className="text-red-400 hover:text-red-300 flex items-center gap-1 font-bold uppercase tracking-wider text-[10px]"
                    >
                      <StopCircle size={12} /> Stop
                    </button>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-cyan-500 h-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleBulkScan}
                  disabled={!urls.trim()}
                  className="w-full py-3 bg-gradient-to-r from-cyan-600 to-violet-600 hover:from-cyan-500 hover:to-violet-500 text-white font-bold rounded-xl shadow-lg shadow-cyan-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {results.length > 0 && stopSignal.current
                    ? "Resume / Restart Scan"
                    : "Start Bulk Scan"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Results Area */}
        <div className="lg:col-span-2">
          <div className="glass-card p-6 rounded-2xl min-h-[500px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Scan Results</h2>
                <p className="text-xs text-slate-500 mt-1">
                  {results.length > 0
                    ? `${results.length} sites processed`
                    : "Ready to scan"}
                  {stopSignal.current && (
                    <span className="text-red-400 ml-2 font-bold">
                      (STOPPED)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={downloadCSV}
                  disabled={results.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  title="Download CSV"
                >
                  <Download size={16} /> Export CSV
                </button>
              </div>
            </div>

            {results.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-xl bg-slate-900/20">
                <UploadCloud size={48} className="mb-4 opacity-20" />
                <p>Waiting for targets...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="text-xs text-slate-400 uppercase bg-slate-900/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 rounded-l-lg">Status</th>
                      <th className="px-4 py-3">URL</th>
                      <th className="px-4 py-3">CMS / Platform</th>
                      <th className="px-4 py-3">Tech Count</th>
                      <th className="px-4 py-3 rounded-r-lg">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {results.map((res, i) => (
                      <tr
                        key={i}
                        className="hover:bg-slate-800/30 transition-colors animate-fade-in"
                      >
                        <td className="px-4 py-3">
                          {res.status === "success" ? (
                            <CheckCircle2
                              size={16}
                              className="text-green-500"
                            />
                          ) : (
                            <X size={16} className="text-red-500" />
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-white truncate max-w-[150px]">
                          {res.url}
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-medium">
                          {res.cms}
                        </td>
                        <td className="px-4 py-3">
                          <span className="bg-slate-800 text-cyan-400 px-2 py-1 rounded text-xs font-bold">
                            {res.tech}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 truncate max-w-[150px]">
                          {res.email}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENT: LANDING PAGE ---
const LandingPage = ({ onStart }) => {
  useSEO(
    "StackRay | Free Website Tech Stack Checker & SEO Analyzer",
    "Identify the technology stack of any website. Detect CMS, frameworks, and contact details with our free, unlimited SEO tool."
  );

  return (
    <div className="flex flex-col animate-fade-in">
      {/* HERO SECTION */}
      <div className="min-h-[90vh] flex flex-col items-center justify-center text-center px-4 relative overflow-hidden pt-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-600/20 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-600/10 rounded-full blur-[100px]"></div>

        <div className="relative z-10 max-w-4xl mx-auto space-y-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 text-cyan-400 text-xs font-mono mb-4">
            <Shield size={12} /> SECURE • ANONYMOUS • FREE
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold leading-tight tracking-tight text-white glow-text">
            Reveal the{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
              Hidden Stack
            </span>
          </h1>

          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed">
            The ultimate web intelligence tool. Identify CMS, frameworks,
            analytics, and contact data in seconds.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <button
              onClick={() => onStart("single")}
              className="w-full sm:w-auto group relative px-8 py-4 bg-gradient-to-r from-cyan-600 to-violet-600 rounded-xl font-bold text-white shadow-lg shadow-violet-900/20 hover:shadow-violet-600/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              <div className="absolute inset-0 bg-white/20 group-hover:translate-x-full duration-1000 transform -skew-x-12 -translate-x-full transition-transform"></div>
              <span className="flex items-center justify-center gap-2 relative z-10">
                <Search size={20} /> Analyze URL
              </span>
            </button>

            {/* Restored Bulk Scan Button */}
            <button
              onClick={() => onStart("bulk")}
              className="w-full sm:w-auto px-8 py-4 glass-card hover:bg-slate-800/50 text-white rounded-xl font-bold hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 group"
            >
              <UploadCloud
                size={20}
                className="text-cyan-400 group-hover:text-cyan-300"
              />{" "}
              Bulk Scan
            </button>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-16 opacity-80">
            {[
              { icon: Code, label: "Frameworks" },
              { icon: Globe, label: "CDN / DNS" },
              { icon: Database, label: "CMS" },
              { icon: Activity, label: "Analytics" },
            ].map((f, i) => (
              <div
                key={i}
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-slate-600 transition-colors"
              >
                <f.icon size={24} className="text-slate-400" />
                <span className="text-sm font-mono text-slate-300">
                  {f.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="py-24 bg-slate-900/50 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              How StackRay Works
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We use advanced fingerprinting to analyze HTML structure, script
              signatures, and global headers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Target Acquisition",
                desc: "Enter any URL. We initiate a secure, non-intrusive connection to fetch public headers and DOM elements.",
                icon: Fingerprint,
              },
              {
                step: "02",
                title: "Pattern Matching",
                desc: "Our engine compares the site against 2,000+ known signatures for CMS, eCommerce platforms, and marketing tools.",
                icon: Activity,
              },
              {
                step: "03",
                title: "Intel Report",
                desc: "Receive a comprehensive breakdown of the tech stack, hosting provider, region, and contact information.",
                icon: FileText,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="relative p-8 rounded-2xl bg-slate-950 border border-slate-800 hover:border-cyan-500/30 transition-all group"
              >
                <div className="absolute top-4 right-4 text-4xl font-bold text-slate-800 select-none">
                  {item.step}
                </div>
                <div className="w-12 h-12 bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-400 mb-6 group-hover:scale-110 transition-transform">
                  <item.icon size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* USE CASES */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Intelligence for Every Role
              </h2>
              <p className="text-slate-400 mb-8 text-lg">
                Whether you are prospecting for sales or analyzing a
                competitor's infrastructure, StackRay provides the data you
                need.
              </p>
              <div className="space-y-6">
                {[
                  {
                    title: "Sales & Marketing",
                    desc: "Find leads using specific technologies and get direct contact emails.",
                    icon: Users,
                  },
                  {
                    title: "Developers",
                    desc: "Analyze how your favorite sites are built. Discover new libraries and frameworks.",
                    icon: Code,
                  },
                  {
                    title: "Security Researchers",
                    desc: "Identify outdated CMS versions and exposed headers quickly.",
                    icon: Shield,
                  },
                ].map((uc, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1">
                      <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-violet-400 border border-slate-700">
                        <uc.icon size={18} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-lg">
                        {uc.title}
                      </h4>
                      <p className="text-slate-500 text-sm">{uc.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-violet-500 rounded-2xl blur-2xl opacity-20"></div>
              <div className="glass-card p-8 rounded-2xl relative border border-slate-700/50">
                <div className="space-y-4 font-mono text-sm">
                  <div className="flex justify-between text-slate-500 border-b border-slate-800 pb-2">
                    <span>TARGET</span>
                    <span>STATUS</span>
                  </div>
                  <div className="flex justify-between text-white">
                    <span>competitor-site.com</span>
                    <span className="text-green-400">ONLINE</span>
                  </div>
                  <div className="h-px bg-slate-800 my-4"></div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400">✓</span> Detected:{" "}
                      <span className="text-white">Shopify Plus</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400">✓</span> Detected:{" "}
                      <span className="text-white">Klaviyo</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-cyan-400">✓</span> Detected:{" "}
                      <span className="text-white">Facebook Pixel</span>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-slate-900 rounded border border-slate-800 text-xs text-slate-400">
                    {`> Extracting contacts...`}
                    <br />
                    {`> Found: `}
                    <span className="text-cyan-400">
                      support@competitor.com
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ SECTION */}
      <FAQSection />

      {/* COMMITMENT SECTION */}
      <div className="py-20 bg-slate-900 border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Infinity className="mx-auto text-white mb-6" size={48} />
          <h2 className="text-3xl font-bold text-white mb-4">
            Free & Unlimited Access
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            We believe powerful intelligence tools should be accessible to
            everyone. Use StackRay as much as you need, for as long as you want.
          </p>
          <div className="flex justify-center gap-4">
            <div className="inline-flex items-center gap-2 text-cyan-400 font-mono text-sm bg-cyan-950/30 px-4 py-2 rounded-full border border-cyan-900/50">
              <ShieldCheck size={16} /> Proprietary Technology
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- APP ROOT ---
export default function App() {
  const [view, setView] = useState("landing"); // landing, analyze, bulk, terms, privacy

  const handleStartAnalyze = (mode) => {
    if (mode === "bulk") {
      setView("bulk");
    } else {
      setView("analyze");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 selection:bg-cyan-500/30 selection:text-cyan-200 flex flex-col">
      <CustomStyles />

      <Navbar setView={setView} />

      <main className="flex-1 flex flex-col w-full">
        {view === "landing" && <LandingPage onStart={handleStartAnalyze} />}
        {view === "analyze" && (
          <AnalyzeView onBack={() => setView("landing")} />
        )}
        {view === "bulk" && (
          <BulkAnalyzeView onBack={() => setView("landing")} />
        )}
        {view === "terms" && (
          <InfoPage
            title="Terms of Use"
            content={TERMS_CONTENT}
            onBack={() => setView("landing")}
          />
        )}
        {view === "privacy" && (
          <InfoPage
            title="Privacy Policy"
            content={PRIVACY_CONTENT}
            onBack={() => setView("landing")}
          />
        )}
      </main>

      {/* Render footer only on main pages, usually nice to hide on deep analyze view but keeping it for consistent navigation */}
      {view !== "analyze" && view !== "bulk" && <Footer setView={setView} />}
      {/* Minimal footer for analyze/bulk view */}
      {(view === "analyze" || view === "bulk") && (
        <footer className="py-6 text-center text-xs text-slate-600 border-t border-slate-900 mt-auto">
          StackRay © 2025 •{" "}
          <button
            onClick={() => setView("terms")}
            className="hover:text-slate-400"
          >
            Terms
          </button>
        </footer>
      )}
    </div>
  );
}
